// GameDevSecurity — admin control room for the Dark Launch sprint.
//
// Two cards on one page:
//   1. PIN Vault — view + rotate the universal developer PIN that
//      unlocks locked lobby games for testing.
//   2. Launch Matrix — per-game is_locked toggles for the full 15-card
//      catalogue. Flipping a switch fires PATCH /api/admin/games/:id
//      immediately and surfaces a toast on success/failure.
//
// Surfaces visible only on /api/admin/* so this route is admin-only by
// virtue of the backend guard; we also lean on the AdminAppShell auth
// gate at the route level (see App.tsx).
//
// Styling mirrors the existing settings pages: arena-tinted glass
// cards, INPUT_CLS for the PIN input, violet accents.
import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import { INPUT_CLS } from '../components/fieldStyles';

type Game = {
  id: string;
  displayName: string;
  tag: string | null;
  isLocked: boolean;
  visible: boolean;
  sortOrder: number;
  themeGradient: string | null;
  themeEmoji: string | null;
  logoPath: string | null;
  unlockHint: string;
};

type PinState = { pin: string | null; updatedAt: string | null };

export default function GameDevSecurity() {
  const { push } = useToast();

  // ─── PIN vault ───────────────────────────────────────────────────
  const [pin, setPin] = useState<PinState | null>(null);
  const [pinLoadErr, setPinLoadErr] = useState<string | null>(null);
  const [draftPin, setDraftPin] = useState('');
  const [savingPin, setSavingPin] = useState(false);
  const [pinTouched, setPinTouched] = useState(false);

  // ─── Game matrix ─────────────────────────────────────────────────
  const [games, setGames] = useState<Game[] | null>(null);
  const [gamesErr, setGamesErr] = useState<string | null>(null);
  // Per-row "in-flight" state so we can disable the toggle while its
  // PATCH is mid-air without freezing the rest of the grid.
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  // Drag-and-drop reorder state. `originalOrder` is the most recent
  // server-confirmed sequence of ids; we compare against the live
  // `games` array to detect dirty state and reveal the Save Order
  // button. `draggingId` highlights the row mid-drag.
  //
  // Implementation note (2026-05-22): switched away from HTML5
  // drag-and-drop to a Pointer Events implementation because the
  // HTML5 API
  //   (a) fights iOS Safari scroll — finger drags scroll the page
  //       instead of moving the item,
  //   (b) auto-selects page text while dragging on mobile,
  //   (c) has no built-in autoscroll, so you can't drag an item to
  //       a position that's currently off-screen.
  // Pointer Events fix all three. The handlers below set up a
  // pointercapture on the drag handle, attach document-level
  // pointermove/pointerup listeners while dragging, autoscroll the
  // window when the pointer nears the top/bottom edges, and apply
  // body { user-select: none; touch-action: none } class so the
  // page can't scroll or select text mid-drag.
  const [originalOrder, setOriginalOrder] = useState<string[]>([]);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const draggingIdRef = useRef<string | null>(null);
  const autoscrollRafRef = useRef<number | null>(null);
  const autoscrollVyRef = useRef(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.get<PinState>('/admin/games/universal-pin');
        setPin(r);
      } catch (e) {
        setPinLoadErr((e as ApiError).message || 'Failed to load PIN');
      }
      try {
        const r = await adminApi.get<{ games: Game[] }>('/admin/games');
        setGames(r.games);
        setOriginalOrder(r.games.map((g) => g.id));
      } catch (e) {
        setGamesErr((e as ApiError).message || 'Failed to load games');
      }
    })();
  }, []);

  const dirtyOrder = (() => {
    if (!games) return false;
    if (games.length !== originalOrder.length) return true;
    for (let i = 0; i < games.length; i++) {
      if (games[i].id !== originalOrder[i]) return true;
    }
    return false;
  })();

  // ─── Pointer-events drag-and-drop ────────────────────────────────
  // The reorder routine — pull the dragged id out, insert immediately
  // before the target id (or at end if target is null). Used by the
  // global pointermove handler below. Idempotent: a no-op when the
  // dragged id is already in the right slot.
  function reorderTo(targetId: string | null) {
    setGames((prev) => {
      if (!prev) return prev;
      const dragId = draggingIdRef.current;
      if (!dragId) return prev;
      const from = prev.findIndex((g) => g.id === dragId);
      if (from === -1) return prev;
      let to: number;
      if (targetId === null) {
        to = prev.length - 1;
      } else if (targetId === dragId) {
        return prev;
      } else {
        to = prev.findIndex((g) => g.id === targetId);
        if (to === -1) return prev;
      }
      if (from === to) return prev;
      const next = prev.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }

  // Autoscroll loop — runs as long as the pointer is within `EDGE` px
  // of the viewport top/bottom while dragging. The velocity scales
  // with edge proximity so the further into the edge the faster it
  // scrolls. Stops cleanly when the pointer moves away or drag ends.
  function tickAutoscroll() {
    const vy = autoscrollVyRef.current;
    if (!vy || !draggingIdRef.current) {
      autoscrollRafRef.current = null;
      return;
    }
    window.scrollBy(0, vy);
    autoscrollRafRef.current = requestAnimationFrame(tickAutoscroll);
  }
  function setAutoscrollVelocity(vy: number) {
    autoscrollVyRef.current = vy;
    if (vy !== 0 && autoscrollRafRef.current == null) {
      autoscrollRafRef.current = requestAnimationFrame(tickAutoscroll);
    }
  }

  function onPointerDownHandle(id: string, e: React.PointerEvent) {
    // Block native scroll/text-select for THIS gesture.
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    draggingIdRef.current = id;
    setDraggingId(id);
    document.body.classList.add('gds-dragging');

    const onMove = (ev: PointerEvent) => {
      // Stop the page from scrolling / selecting while dragging.
      ev.preventDefault();

      // Find the row currently under the pointer. We mark each row
      // with `data-game-row="<id>"`; walk up from the topmost element
      // until we find one.
      const stack = document.elementsFromPoint?.(ev.clientX, ev.clientY) || [];
      let targetId: string | null = null;
      for (const el of stack) {
        const rowId = (el as HTMLElement).closest?.('[data-game-row]')
          ?.getAttribute('data-game-row');
        if (rowId) { targetId = rowId; break; }
      }
      if (targetId) reorderTo(targetId);

      // Edge-proximity autoscroll. EDGE is generous (96 px) so a
      // finger on a phone has room to drift the item past the top
      // or bottom of the scrollport.
      const EDGE = 96;
      const MAX_SPEED = 18;
      const vh = window.innerHeight;
      if (ev.clientY < EDGE) {
        const intensity = (EDGE - ev.clientY) / EDGE;     // 0..1
        setAutoscrollVelocity(-Math.ceil(intensity * MAX_SPEED));
      } else if (ev.clientY > vh - EDGE) {
        const intensity = (ev.clientY - (vh - EDGE)) / EDGE;
        setAutoscrollVelocity(Math.ceil(intensity * MAX_SPEED));
      } else {
        setAutoscrollVelocity(0);
      }
    };

    const onUp = () => {
      draggingIdRef.current = null;
      setDraggingId(null);
      setAutoscrollVelocity(0);
      if (autoscrollRafRef.current != null) {
        cancelAnimationFrame(autoscrollRafRef.current);
        autoscrollRafRef.current = null;
      }
      document.body.classList.remove('gds-dragging');
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };

    document.addEventListener('pointermove', onMove, { passive: false });
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  }

  async function saveOrder() {
    if (!games || savingOrder || !dirtyOrder) return;
    setSavingOrder(true);
    try {
      const orderedIds = games.map((g) => g.id);
      const r = await adminApi.post<{ games: Game[] }>('/admin/games/reorder', { orderedIds });
      setGames(r.games);
      setOriginalOrder(r.games.map((g) => g.id));
      push('success', 'Game order saved.');
    } catch (e) {
      push('error', (e as ApiError).message || 'Failed to save order.');
    } finally {
      setSavingOrder(false);
    }
  }

  function resetOrder() {
    if (!games) return;
    // Restore the games array to the original server order.
    const byId = new Map(games.map((g) => [g.id, g] as const));
    const restored = originalOrder.map((id) => byId.get(id)).filter((g): g is Game => !!g);
    setGames(restored);
  }

  // ─── PIN draft validation ────────────────────────────────────────
  // Client-side mirror of the server-side Zod check (4-8 digits). The
  // server is still the authority — this just keeps the Save button
  // disabled until the input is plausibly valid, so the user gets
  // feedback before the round trip.
  const draftValid = /^\d{4,8}$/.test(draftPin);
  const pinDirty = draftPin.length > 0 && draftPin !== pin?.pin;

  async function savePin() {
    if (!draftValid) {
      setPinTouched(true);
      return;
    }
    setSavingPin(true);
    try {
      const r = await adminApi.put<PinState>('/admin/games/universal-pin', { pin: draftPin });
      setPin(r);
      setDraftPin('');
      setPinTouched(false);
      push('success', 'Universal PIN updated.');
    } catch (e) {
      const err = e as ApiError;
      // Most likely a validation_error from the server (4–8 digits
      // enforced server-side); show the message verbatim.
      push('error', err.message || 'Failed to update PIN.');
    } finally {
      setSavingPin(false);
    }
  }

  // ─── Per-game unlock hint editor ─────────────────────────────────
  // Auto-saves on blur. Skipped if the value is unchanged so we don't
  // fire a PATCH every time the admin tabs through the field. Empty
  // input falls back to the server default ('Unlock on Level 3') —
  // see the trim() in the controller.
  async function saveHint(g: Game, nextHint: string) {
    const trimmed = nextHint.slice(0, 120);
    if (trimmed === g.unlockHint) return;
    try {
      const r = await adminApi.patch<{ game: Game }>(
        `/admin/games/${g.id}`,
        { unlock_hint: trimmed },
      );
      setGames((prev) => (prev ? prev.map((x) => (x.id === g.id ? r.game : x)) : prev));
      push('success', `${g.displayName} hint updated.`);
    } catch (e) {
      push('error', (e as ApiError).message || `Failed to update ${g.displayName} hint.`);
    }
  }

  // ─── Toggle handler ──────────────────────────────────────────────
  async function toggleLocked(g: Game) {
    if (busyIds.has(g.id)) return;
    const nextLocked = !g.isLocked;
    setBusyIds((s) => new Set(s).add(g.id));
    // Optimistic flip so the UI feels responsive on slow connections.
    setGames((prev) =>
      prev ? prev.map((x) => (x.id === g.id ? { ...x, isLocked: nextLocked } : x)) : prev,
    );
    try {
      const r = await adminApi.patch<{ game: Game }>(`/admin/games/${g.id}`, { is_locked: nextLocked });
      // Reconcile with the server's view (in case sort_order or other
      // fields change later — defensive sync).
      setGames((prev) => (prev ? prev.map((x) => (x.id === g.id ? r.game : x)) : prev));
      push('success', `${g.displayName} is now ${nextLocked ? 'locked' : 'live'}.`);
    } catch (e) {
      // Roll back the optimistic flip.
      setGames((prev) =>
        prev ? prev.map((x) => (x.id === g.id ? { ...x, isLocked: g.isLocked } : x)) : prev,
      );
      push('error', (e as ApiError).message || `Failed to update ${g.displayName}.`);
    } finally {
      setBusyIds((s) => {
        const next = new Set(s);
        next.delete(g.id);
        return next;
      });
    }
  }

  // GM-1: HIDE toggle — independent of lock. Hidden = removed from the player lobby
  // entirely; the game is still fully administrable here.
  async function toggleVisible(g: Game) {
    if (busyIds.has(g.id)) return;
    const nextVisible = !g.visible;
    setBusyIds((s) => new Set(s).add(g.id));
    setGames((prev) => (prev ? prev.map((x) => (x.id === g.id ? { ...x, visible: nextVisible } : x)) : prev));
    try {
      const r = await adminApi.patch<{ game: Game }>(`/admin/games/${g.id}`, { visible: nextVisible });
      setGames((prev) => (prev ? prev.map((x) => (x.id === g.id ? r.game : x)) : prev));
      push('success', `${g.displayName} is now ${nextVisible ? 'shown to players' : 'hidden from players'}.`);
    } catch (e) {
      setGames((prev) => (prev ? prev.map((x) => (x.id === g.id ? { ...x, visible: g.visible } : x)) : prev));
      push('error', (e as ApiError).message || `Failed to update ${g.displayName}.`);
    } finally {
      setBusyIds((s) => { const next = new Set(s); next.delete(g.id); return next; });
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-3 px-1">
        <span className="grid place-items-center w-10 h-10 rounded-2xl
                         bg-rose-100 dark:bg-rose-500/15
                         text-rose-700 dark:text-rose-300
                         ring-1 ring-inset ring-rose-300/40 dark:ring-rose-400/30">
          <Icon name="shield_lock" filled size={20} />
        </span>
        <div>
          <h1 className="text-lg font-bold text-arena-900 dark:text-white">
            Game Management & Dev Security
          </h1>
          <p className="text-[12px] text-arena-600 dark:text-neon-200/70 leading-snug">
            Lock or unlock lobby cards · rotate the universal developer PIN
          </p>
        </div>
      </header>

      {/* ─── PIN Vault ─────────────────────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl p-5
                   bg-white dark:bg-arena-700/40
                   ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                   backdrop-blur"
      >
        <div className="flex items-center gap-2 mb-3">
          <Icon name="vpn_key" size={16} className="text-violet-600 dark:text-violet-300" />
          <h2 className="text-[13px] font-bold uppercase tracking-wider text-arena-700 dark:text-neon-200/85">
            PIN Vault
          </h2>
        </div>
        <p className="text-[12.5px] text-arena-600 dark:text-neon-200/70 leading-snug mb-4">
          Developers tap the padlock on a locked game 5 times and enter this PIN to unlock it
          for testing. Rotate the value whenever it leaks. Stored as plaintext intentionally —
          it's a shared internal secret, not a user credential.
        </p>

        {/* Big mono display of the current PIN. */}
        <div className="rounded-2xl px-5 py-5 mb-4
                        bg-arena-50 dark:bg-arena-900/60
                        ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                        text-center">
          <div className="text-[10px] uppercase tracking-[.32em] font-bold text-arena-500 dark:text-neon-200/55 mb-1">
            Current PIN
          </div>
          {pinLoadErr ? (
            <div className="text-[12px] text-rose-700 dark:text-rose-300">{pinLoadErr}</div>
          ) : pin === null ? (
            <div className="text-[12px] text-arena-500 dark:text-neon-200/55">Loading…</div>
          ) : pin.pin ? (
            <div className="font-mono text-4xl sm:text-5xl tabular-nums tracking-[.3em]
                            text-arena-900 dark:text-white">
              {pin.pin}
            </div>
          ) : (
            <div className="text-[13px] text-rose-700 dark:text-rose-300">
              PIN not set — set one below.
            </div>
          )}
          {pin?.updatedAt && (
            <div className="text-[10.5px] text-arena-500 dark:text-neon-200/50 mt-2">
              Updated {new Date(pin.updatedAt).toLocaleString()}
            </div>
          )}
        </div>

        {/* Rotate input + save. */}
        <div className="space-y-2">
          <label className="text-[12px] font-semibold text-arena-700 dark:text-neon-200/85">
            Set a new PIN
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={8}
              value={draftPin}
              onChange={(e) => {
                setDraftPin(e.target.value.replace(/\D/g, '').slice(0, 8));
                setPinTouched(true);
              }}
              placeholder="4–8 digits"
              className={INPUT_CLS + ' font-mono tracking-widest text-lg flex-1'}
            />
            <button
              type="button"
              onClick={savePin}
              disabled={savingPin || !pinDirty || !draftValid}
              className="inline-flex items-center justify-center gap-1.5
                         rounded-xl px-4 py-2 text-sm font-bold
                         text-white
                         bg-gradient-to-b from-violet-500 to-violet-700
                         hover:from-violet-400 hover:to-violet-600
                         shadow-[0_4px_14px_rgba(139,92,246,0.35)]
                         disabled:opacity-50 disabled:cursor-not-allowed
                         active:scale-[.98] transition"
            >
              {savingPin ? (
                <><Icon name="progress_activity" size={16} className="animate-spin" /> Saving…</>
              ) : (
                <><Icon name="save" size={16} /> Save PIN</>
              )}
            </button>
          </div>
          {/* Validation hint — only shown once the user touches the
              field, so the page doesn't open with a scary red message. */}
          {pinTouched && draftPin.length > 0 && !draftValid && (
            <p className="text-[11.5px] text-rose-700 dark:text-rose-300">
              PIN must be 4–8 digits.
            </p>
          )}
          <p className="text-[11px] text-arena-500 dark:text-neon-200/55">
            The new PIN takes effect immediately. Existing dev grants stay valid for their
            remaining 1-hour TTL.
          </p>
        </div>
      </motion.section>

      {/* ─── Launch Matrix ─────────────────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.04 }}
        className="rounded-2xl p-5
                   bg-white dark:bg-arena-700/40
                   ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                   backdrop-blur"
      >
        <div className="flex items-center gap-2 mb-3">
          <Icon name="dashboard" size={16} className="text-emerald-600 dark:text-emerald-300" />
          <h2 className="text-[13px] font-bold uppercase tracking-wider text-arena-700 dark:text-neon-200/85">
            Launch Matrix
          </h2>
          {games && (
            <span className="ml-auto text-[11px] text-arena-500 dark:text-neon-200/55">
              {games.filter((g) => !g.isLocked).length} live · {games.filter((g) => g.isLocked).length} locked
            </span>
          )}
        </div>
        <p className="text-[12.5px] text-arena-600 dark:text-neon-200/70 leading-snug mb-4">
          Toggle each card's visibility on the player lobby. Drag the ⋮⋮ handle on any row
          to reorder — the lobby reflects the new order once you save.
        </p>

        {gamesErr && (
          <div className="rounded-xl px-3 py-2 mb-3 text-[12.5px]
                          bg-rose-50 dark:bg-rose-500/10
                          text-rose-700 dark:text-rose-300
                          ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30">
            {gamesErr}
          </div>
        )}

        {games === null && !gamesErr ? (
          <div className="text-[13px] text-arena-500 dark:text-neon-200/55">Loading…</div>
        ) : games && games.length === 0 ? (
          <div className="text-[13px] text-arena-500 dark:text-neon-200/55">No games registered.</div>
        ) : games ? (
          <>
            <ul className="divide-y divide-arena-200/60 dark:divide-arena-600/40">
              {games.map((g) => (
                <GameToggleRow
                  key={g.id}
                  game={g}
                  busy={busyIds.has(g.id)}
                  dragging={draggingId === g.id}
                  onToggle={() => toggleLocked(g)}
                  onToggleVisible={() => toggleVisible(g)}
                  onHandlePointerDown={(e) => onPointerDownHandle(g.id, e)}
                  onHintSave={(next) => saveHint(g, next)}
                />
              ))}
            </ul>
            {/* Save Order action bar — only revealed when the visible
                order differs from the last server-confirmed order.
                Animates in from the bottom so it doesn't shift other
                Settings cards when idle. */}
            {dirtyOrder && (
              <div className="mt-4 flex items-center gap-2 pt-3 border-t border-arena-200/60 dark:border-arena-600/40">
                <span className="text-[12px] text-arena-600 dark:text-neon-200/75">
                  Order changed — save to apply on the player lobby.
                </span>
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={resetOrder}
                  disabled={savingOrder}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-semibold
                             text-arena-700 dark:text-neon-200/80
                             bg-arena-100 dark:bg-arena-700/60
                             hover:bg-arena-200 dark:hover:bg-arena-700
                             disabled:opacity-50 transition"
                >
                  <Icon name="undo" size={14} /> Reset
                </button>
                <button
                  type="button"
                  onClick={saveOrder}
                  disabled={savingOrder}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[12px] font-bold
                             text-white
                             bg-gradient-to-b from-emerald-500 to-emerald-700
                             hover:from-emerald-400 hover:to-emerald-600
                             shadow-[0_4px_14px_rgba(16,185,129,0.35)]
                             disabled:opacity-50 active:scale-[.98] transition"
                >
                  {savingOrder ? (
                    <><Icon name="progress_activity" size={14} className="animate-spin" /> Saving…</>
                  ) : (
                    <><Icon name="save" size={14} /> Save Order</>
                  )}
                </button>
              </div>
            )}
          </>
        ) : null}
      </motion.section>
    </div>
  );
}

// ─── Toggle row ─────────────────────────────────────────────────────

function GameToggleRow({
  game, busy, dragging, onToggle, onToggleVisible, onHandlePointerDown, onHintSave,
}: {
  game: Game;
  busy: boolean;
  dragging: boolean;
  onToggle: () => void;
  onToggleVisible: () => void;
  onHandlePointerDown: (e: React.PointerEvent) => void;
  onHintSave: (next: string) => void;
}) {
  // Local input state — committed to the server on blur / Enter so we
  // don't fire a PATCH on every keystroke. Resyncs if the prop changes
  // (e.g., after a server-side reorder refreshed the row).
  const [hintDraft, setHintDraft] = useState(game.unlockHint);
  useEffect(() => { setHintDraft(game.unlockHint); }, [game.unlockHint]);
  return (
    <li
      data-game-row={game.id}
      className={`flex items-center gap-3 py-3 transition
                  ${dragging ? 'opacity-40' : 'opacity-100'}`}
    >
      {/* Drag handle — only this element receives the pointerdown
          gesture, so a tap on the rest of the row still hits the
          lock toggle. `touchAction: none` is the critical iOS bit:
          without it, the browser claims the gesture as a scroll
          before our onPointerDown can call preventDefault. */}
      <button
        type="button"
        onPointerDown={onHandlePointerDown}
        aria-label={`Drag to reorder ${game.displayName}`}
        title="Drag to reorder"
        style={{ touchAction: 'none' }}
        className="shrink-0 grid place-items-center w-7 h-9 rounded
                   text-arena-400 hover:text-arena-700
                   dark:text-neon-200/45 dark:hover:text-neon-200/85
                   cursor-grab active:cursor-grabbing
                   hover:bg-arena-100/60 dark:hover:bg-arena-700/40 transition
                   select-none"
      >
        <Icon name="drag_indicator" size={20} />
      </button>
      {/* Logo thumbnail — same render rule as the player lobby:
          gradient base + logo artwork on top + dark veil for locked
          tiles. Admins see the actual game artwork for every row,
          dimmed for locked, so the Launch Matrix reads at a glance. */}
      <div
        className="relative w-11 h-11 rounded-xl overflow-hidden
                   ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 shrink-0"
      >
        <div
          className={`absolute inset-0 bg-gradient-to-br ${game.themeGradient || 'from-arena-400 to-arena-600'}`}
          aria-hidden
        />
        {game.logoPath ? (
          <img
            src={game.logoPath}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <span className="absolute inset-0 grid place-items-center text-xl" aria-hidden>
            {game.themeEmoji || '🎮'}
          </span>
        )}
        {game.isLocked && (
          <div
            className="absolute inset-0 bg-black/55"
            style={{ backdropFilter: 'saturate(0.65)' }}
            aria-hidden
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-bold text-[14px] text-arena-900 dark:text-white truncate">
            {game.displayName}
          </span>
          {game.tag && (
            <span className="px-1.5 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider
                             bg-arena-100 dark:bg-arena-600/40
                             text-arena-600 dark:text-neon-200/75">
              {game.tag}
            </span>
          )}
        </div>
        <div className="text-[11.5px] mt-0.5 text-arena-500 dark:text-neon-200/65">
          {game.isLocked
            ? <>🔒 Locked</>
            : <span className="text-emerald-600 dark:text-emerald-300 font-semibold">● Live in lobby</span>}
        </div>
        {/* Per-game unlock-hint editor. Always editable (so the admin
            can pre-write the text before a game is locked again), but
            the hint is only DISPLAYED to players on the lobby when
            the game is locked. The current text is shown in the
            input; tabbing / clicking out commits any change. */}
        <label className="block mt-1.5">
          <span className="block text-[9.5px] uppercase tracking-wider font-bold text-arena-400 dark:text-neon-200/50 mb-0.5">
            Locked-tile hint
          </span>
          <input
            type="text"
            value={hintDraft}
            maxLength={120}
            onChange={(e) => setHintDraft(e.target.value)}
            onBlur={() => onHintSave(hintDraft)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); (e.currentTarget as HTMLInputElement).blur(); }
              if (e.key === 'Escape') { setHintDraft(game.unlockHint); (e.currentTarget as HTMLInputElement).blur(); }
            }}
            placeholder="Unlock on Level 3"
            className="w-full max-w-xs rounded-md px-2 py-1 text-[12px]
                       bg-white dark:bg-arena-900/60
                       border border-arena-200/70 dark:border-arena-600/50
                       text-arena-900 dark:text-white
                       placeholder-arena-400 dark:placeholder-neon-200/40
                       focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400
                       transition"
          />
        </label>
      </div>

      {/* GM-1: HIDE control — pulls the game off the player lobby entirely,
          independent of the lock switch. Amber pill when hidden. */}
      <button
        type="button"
        onClick={onToggleVisible}
        disabled={busy}
        title={game.visible ? 'Visible to players — click to HIDE from the lobby' : 'Hidden from the lobby — click to SHOW'}
        className={`shrink-0 px-2.5 py-1 rounded-lg text-[11px] font-bold transition disabled:opacity-50
                    ${game.visible
                      ? 'bg-arena-100 text-arena-600 dark:bg-arena-700/60 dark:text-neon-200/70'
                      : 'bg-amber-500 text-white'}`}
      >
        {game.visible ? '👁 Shown' : '🚫 Hidden'}
      </button>

      {/* iOS/Material-style toggle. Color flips between emerald (live)
          and arena-grey (locked); the thumb slides on the track. */}
      <button
        type="button"
        onClick={onToggle}
        disabled={busy}
        role="switch"
        aria-checked={!game.isLocked}
        aria-label={`Toggle ${game.displayName} ${game.isLocked ? 'live' : 'locked'}`}
        className={`relative shrink-0 h-7 w-12 rounded-full transition
                    ring-1 ring-inset
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${!game.isLocked
                      ? 'bg-emerald-500 ring-emerald-400/40 dark:bg-emerald-500'
                      : 'bg-arena-300 ring-arena-300/60 dark:bg-arena-600 dark:ring-arena-500/40'}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform
                      ${!game.isLocked ? 'translate-x-5' : 'translate-x-0'}`}
          aria-hidden
        />
      </button>
    </li>
  );
}
